export type IRmaError = {
  title: string
  message: string
}


export class RmaError extends Error {
  public readonly status: number
  public readonly title: string

  constructor(status: number, title: string, message: string) {
    super(message)

    this.name = this.constructor.name
    this.status = status
    this.title = title
  }
}


export class BadRequestError extends RmaError {
  constructor(title: string, message: string) {
    super(400, title, message)
  }
}


export class UnauthorizedError extends RmaError {
  constructor(title: string, message: string) {
    super(401, title, message)
  }
}


export type StatusCodeToError<T extends number> =
  T extends 400 ? BadRequestError
    : T extends 401 ? UnauthorizedError
      : RmaError


const statusToErrorClassMap: Record<number, new (title: string, message: string) => RmaError> = {
  400: BadRequestError,
  401: UnauthorizedError,
}


const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

// Best-effort extraction: the backend may send {title, message}, {message}
// only, a bare string, or an HTML error page. Never throw — an error response
// must always yield a usable RmaError, not a crash, so failures surface in the
// UI instead of looking like success.
const extractErrorParts = (data: unknown): { title: string, message: string } => {
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const message = asString(record['message']) ?? asString(record['title'])
    if (message) return { title: asString(record['title']) ?? '', message }
  }
  const text = asString(data)
  // A raw HTML body carries no useful message for the user; keep it generic.
  if (text && !text.trimStart().startsWith('<')) return { title: '', message: text }
  return { title: '', message: 'Something went wrong. Please try again.' }
}

// The executor decides whether a status is expected; this just builds the error.
export function parseRmaError(status: number, data: unknown): RmaError {
  const { title, message } = extractErrorParts(data)

  const ErrorClass = statusToErrorClassMap[status]

  return ErrorClass
    ? new ErrorClass(title, message)
    : new RmaError(status, title, message)
}
